import { Box, Button, Paper, Typography } from '@material-ui/core';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import StoryItem from '../../components/StoryItem';
import { SubmitControl } from '../../components/SubmitControl';
import { useBundlePackage, useBundlePackageMutation } from '../../hooks/queries/useBundlePackages';
import { getStoryById } from '../../services/api/Story';
import settings from '../../services/settings';
import { IBundlePackage, IStory } from '../../services/types';
import StorySelectModal from '../../components/StorySelectModal';
import BundlePackageItem, { getInitialBundle } from './BundlePackageItem';
import useStyles from './styles';

interface IProps {
  storyId?: number;
  onCreate?: (bundles: IBundlePackage[]) => void;
}

const BundlePackageView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { storyId, onCreate } = props;

  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);
  const [story, setStory] = useState<IStory>();
  const [bundles, setBundles] = useState<IBundlePackage[]>([getInitialBundle()]);
  const originalStory = useRef<IStory>();

  const { data: cachedBundles } = useBundlePackage({ storyId }, { enabled: !!storyId });
  const { createOrUpdate } = useBundlePackageMutation();

  const [validationMessages, setValidationMessages] = useState<Set<string>>(new Set<string>());
  const [submitControlStatus, setSubmitControlStatus] = useState<'create' | 'view' | 'edit'>(!storyId ? 'create' : 'view');

  const handleStorySelect = (story: IStory) => {
    setStory(story);

    setBundles(ps => {
      const bundles = [...ps];
      bundles.forEach(bundle => (bundle.storyId = story.id));

      return bundles;
    });

    setIsStoryModalOpen(false);
  };

  const handleSubmitClick = async () => {
    if (!story) {
      return;
    }

    const payload = { storyId: story.id, bundles: [...bundles] };
    payload.bundles.forEach(bundle => (bundle.prices = bundle.prices.filter(price => price.episodeNum !== 0)));

    const data = await createOrUpdate.mutateAsync(payload);

    reset();

    if (submitControlStatus === 'edit') {
      setBundles(data);
    }

    if (onCreate) {
      onCreate(data);
    }
  };

  const handleBundlePackageItemChange = (bundles: IBundlePackage[]) => setBundles(bundles);

  const handleValidate = useCallback((messages: string[]) => {
    setValidationMessages(new Set([...messages]));
  }, []);

  const handleSelectStoryClick = () => setIsStoryModalOpen(true);

  const handleEditClick = () => {
    setSubmitControlStatus('edit');
    originalStory.current = story;
  };

  const handleCancelClick = () => {
    reset();
  };

  const handleStoryItemClick = () => {
    if (!story) {
      return;
    }

    window.open(`${settings.getCrewURI()}/stories/${story.id}`);
  };

  const reset = () => {
    setStory(originalStory.current);

    if (!cachedBundles || cachedBundles.length === 0) {
      setBundles([getInitialBundle()]);
    } else {
      setBundles(cachedBundles);
    }

    setSubmitControlStatus(ps => (ps === 'edit' ? 'view' : 'create'));
  };

  useEffect(() => {
    if (storyId && cachedBundles) {
      setBundles(cachedBundles);

      const fetchStory = async () => {
        const { data: story } = await getStoryById(storyId);
        setStory(story);
      };

      fetchStory();
    }
  }, [storyId, cachedBundles]);

  return (
    <Box>
      <Box display="flex" alignItems="center" paddingBottom={2}>
        <Typography variant="h6">{submitControlStatus === 'create' ? 'Create Bundle Package' : `${story?.title || ''} Bundle Package`}</Typography>
        <SubmitControl
          mode={submitControlStatus}
          onEditClick={handleEditClick}
          validationMessages={[...validationMessages]}
          onSubmitClick={handleSubmitClick}
          onCancelClick={handleCancelClick}
        />
      </Box>
      <Box className={classes.wrap}>
        {submitControlStatus === 'create' && (
          <Box>
            <Button color="primary" variant="contained" onClick={handleSelectStoryClick}>
              Select a story
            </Button>
          </Box>
        )}
        {story && (
          <Fragment>
            <Box marginY={2} width={400}>
              <StoryItem
                story={story}
                readonly={true}
                onItemClick={handleStoryItemClick}
                extraText={`Episode count = ${story.episodeCount} | Episode price = ${story.episodePrice || 3}`}
              />
            </Box>
            <BundlePackageItem
              story={story}
              bundles={bundles}
              isEditing={submitControlStatus !== 'view'}
              onValidate={handleValidate}
              onChange={handleBundlePackageItemChange}
            />
          </Fragment>
        )}
        {!story && submitControlStatus === 'create' && (
          <Paper className={classes.storyPlaceholder}>
            <Typography variant="caption">To set the price for the bundle package, select a story first.</Typography>
          </Paper>
        )}
      </Box>
      <StorySelectModal
        open={isStoryModalOpen}
        onClick={handleStorySelect}
        onClose={() => setIsStoryModalOpen(false)}
        title="Select a story"
        filter="story"
      />
    </Box>
  );
};

export default BundlePackageView;
